import { useState, useEffect, useRef, useMemo } from 'react';
import { Mail, Plus, CreditCard as Edit, Trash2, Save, ArrowLeft, Settings, Eye, Code2, BookOpen } from 'lucide-react';
import { EmailTemplate } from '../../types/crm';
import { supabaseEmailTemplateStorage } from '../../services/supabaseStorage';
import { useToast } from '../../contexts/ToastContext';
import { PlaceholderService } from '../../services/placeholderService';
import { EmailEditor, EmailEditorHandle } from '../ui/EmailEditor';
import { FloatingWorkspacePanel } from '../ui/FloatingWorkspacePanel';
import { ManageCourseEmailContentModal } from './ManageCourseEmailContentModal';

interface ManageEmailTemplatesModalProps {
  onClose: () => void;
  onTemplatesUpdated: () => void;
}

const PLACEHOLDER_META: Array<{ token: string; description: string }> = [
  { token: '{{ContactFirstName}}', description: 'Contact first name' },
  { token: '{{ContactLastName}}', description: 'Contact last name' },
  { token: '{{ContactEmail}}', description: 'Contact email address' },
  { token: '{{ContactPhoneNumber}}', description: 'Contact phone number' },
  { token: '{{CompanyName}}', description: 'Customer / company name' },
  { token: '{{CourseName}}', description: 'Course name' },
  { token: '{{CourseLanguage}}', description: 'Course delivery language' },
  { token: '{{Language}}', description: 'Alias for course delivery language' },
  { token: '{{CourseDate}}', description: 'Primary course date' },
  { token: '{{StartTime}}', description: 'Session start time' },
  { token: '{{EndTime}}', description: 'Session end time' },
  { token: '{{TotalPrice}}', description: 'Total booking price' },
  { token: '{{CourseFee}}', description: 'Course fee (includes €)' },
  { token: '{{AmountPaid}}', description: 'Amount paid so far (includes €)' },
  { token: '{{InstructorPrice}}', description: 'Instructor fee' },
  { token: '{{ClientPO}}', description: 'Client purchase order' },
  { token: '{{InstructorPO}}', description: 'Instructor purchase order' },
  { token: '{{CompanyAddressLine1}}', description: 'Company address line 1' },
  { token: '{{CompanyAddressLine2}}', description: 'Company address line 2' },
  { token: '{{CompanyCity}}', description: 'Company city' },
  { token: '{{TimeBasedGreeting}}', description: 'Good morning / afternoon greeting' },
  { token: '{{AllBookingDates}}', description: 'All scheduled training dates' },
  { token: '{{NextUpcomingDate}}', description: 'Next upcoming session date' },
  { token: '{{CourseDetailsListHTML}}', description: 'HTML list of course details' },
  { token: '{{Location}}', description: 'Formatted training location address' },
  { token: '{{LocationAddress}}', description: 'Location address line 1' },
  { token: '{{LocationAddressMultiline}}', description: 'Full address on multiple lines' },
  { token: '{{LocationEircode}}', description: 'Location Eircode / postcode' },
  { token: '{{LocationNotes}}', description: 'Location notes (venue instructions)' },
  { token: '{{LocationOrOnsite}}', description: 'Location address or "Onsite"' },
  { token: '{{LocationContactName}}', description: 'Location contact name' },
  { token: '{{LocationContactPhone}}', description: 'Location contact phone' },
  { token: '{{LocationContactEmail}}', description: 'Location contact email' },
  { token: '{{BookingReference}}', description: 'Booking reference code (e.g. TT00183)' },
  { token: '{{ReferenceNumber}}', description: 'Alias for booking / session reference' },
  { token: '{{CourseSessionReference}}', description: 'Course session reference code (e.g. TT00183)' },
  { token: '{{CustomerReferenceId}}', description: 'Customer ID (CRM UUID)' },
  { token: '{{AttendeeCount}}', description: 'Number of attendees on the session' },
  { token: '{{ParticipantList}}', description: 'Comma-separated participant names (company booking emails)' },
  { token: '{{ParticipantCount}}', description: 'Number of participants in a company cohort' },
  { token: '{{AttendeesList}}', description: 'Alias for participant / attendee name list' },
  {
    token: '{{CourseEmailContent}}',
    description: 'Shared HTML block for the course and session delivery mode (Manage → Course email wording)',
  },
  { token: '{{signatureUrl}}', description: 'Email signature image URL' },
];

const SAMPLE_CONTEXT = {
  customer: {
    id: 'sample-id',
    companyName: 'Sample Company Ltd',
    industry: 'Manufacturing',
    generalNotes: '',
    locations: [{
      id: 'location-id',
      addressLine1: 'Office 15, Dunshaughlin Business Park',
      addressLine2: '',
      addressLine3: '',
      city: 'Dunshaughlin',
      countyState: 'Meath',
      eircodePostcode: 'A85 EE98',
      country: 'Ireland',
      locationContactName: 'Collier Training Solutions',
      locationContactEmail: 'info@colliertraining.ie',
      locationContactPhone: '085 8244 628',
      locationNotes: '',
    }],
    contacts: [],
    paymentHistory: [],
    generatedCertificates: [],
  },
  contact: {
    id: 'contact-id',
    firstName: 'John',
    lastName: 'Smith',
    role: 'Safety Manager',
    email: 'john.smith@samplecompany.com',
    phoneNumber: '087 123 4567',
    isBillingContact: false,
    isGeneralContact: true,
    contactNotes: '',
  },
  instructor: {
    id: 'instructor-id',
    firstName: 'Davor',
    lastName: 'Antonic',
    email: 'davor@colliertraining.ie',
    createdAt: '',
    updatedAt: '',
  },
  location: {
    id: 'location-id',
    name: 'Ireland T&T',
    addressLine1: '11C Parkmore Industrial Estate, Long Mile Road',
    addressLine2: '',
    addressLine3: '',
    city: 'Dublin 12',
    countyState: 'Dublin',
    eircodePostcode: 'D12 RH39',
    country: 'Ireland',
    locationContactName: 'Sylvia',
    locationContactEmail: 'info@irelandtt.ie',
    locationContactPhone: '0871234567',
    locationNotes: 'Ask reception for Conference Room B',
  },
  bookingData: {
    courseName: 'Forklift Training',
    courseLanguage: 'English',
    courseDate: '2025-02-15',
    startTime: '09:00',
    endTime: '17:00',
    totalPrice: 450.0,
    instructorPrice: 300.0,
    instructorPO: 'INS-2025-001',
    clientPO: 'PO-12345',
    selectedLocationId: 'location-id',
    selectedInstructorId: 'instructor-id',
    bookingReference: 'TT00183',
    courseSessionReference: 'TT00183',
    allBookingDates: ['2025-02-15', '2025-02-16'],
    allBookingDatesWithTimes: [
      { date: '2025-02-15', startTime: '09:00', endTime: '17:00' },
      { date: '2025-02-16', startTime: '09:00', endTime: '17:00' },
    ],
  },
  courseLanguage: 'English',
  courseFee: '€160.00',
  amountPaid: '€0.00',
  locationEircode: 'D12 RH39',
  locationNotes: 'Ask reception for Conference Room B',
  courseSessionReference: 'TT00183',
  bookingReference: 'TT00183',
  customerId: 'sample-id',
  courseEmailContent:
    '<p><em>(Course-specific content for Forklift Training — assigned wording block)</em></p>',
};

function formatSamplePreview(value: string, max = 100): string {
  const plain = value.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  if (!plain) return '(empty)';
  if (plain.length <= max) return plain;
  return `${plain.slice(0, max)}…`;
}

export function ManageEmailTemplatesModal({ onClose, onTemplatesUpdated }: ManageEmailTemplatesModalProps) {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    body: '',
    isDefault: false,
  });
  const [showVisualEditor, setShowVisualEditor] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showCourseWordingModal, setShowCourseWordingModal] = useState(false);
  const { showSuccess, showError } = useToast();
  const emailEditorRef = useRef<EmailEditorHandle>(null);
  const subjectInputRef = useRef<HTMLInputElement>(null);
  const lastFocusedFieldRef = useRef<'body' | 'subject' | 'html'>('html');

  const samplePlaceholders = useMemo(
    () => PlaceholderService.generatePlaceholders(SAMPLE_CONTEXT as any),
    []
  );

  const previewHtml = useMemo(() => {
    if (!formData.body.trim()) {
      return '<p style="color: #94a3b8; padding: 20px;">Email content will appear here…</p>';
    }
    return PlaceholderService.replacePlaceholders(formData.body, samplePlaceholders as any);
  }, [formData.body, samplePlaceholders]);

  const previewSubject = useMemo(() => {
    if (!formData.subject.trim()) return '(No subject)';
    return PlaceholderService.replacePlaceholders(formData.subject, samplePlaceholders as any);
  }, [formData.subject, samplePlaceholders]);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setIsLoading(true);
      const loadedTemplates = await supabaseEmailTemplateStorage.getEmailTemplates();
      setTemplates(loadedTemplates);
    } catch (error) {
      showError('Failed to load email templates');
      console.error('Error loading templates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) newErrors.name = 'Template name is required';
    if (!formData.subject.trim()) newErrors.subject = 'Email subject is required';
    if (!formData.body.trim()) newErrors.body = 'Email body is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      const template: EmailTemplate = {
        id: editingTemplate?.id || crypto.randomUUID(),
        name: formData.name.trim(),
        subject: formData.subject.trim(),
        body: formData.body.trim(),
        isDefault: formData.isDefault,
      };

      if (editingTemplate) {
        await supabaseEmailTemplateStorage.updateEmailTemplate(template);
        setTemplates((prev) => prev.map((t) => (t.id === template.id ? template : t)));
        showSuccess('Template updated successfully');
      } else {
        await supabaseEmailTemplateStorage.addEmailTemplate(template);
        setTemplates((prev) => [...prev, template]);
        showSuccess('Template created successfully');
      }

      setEditingTemplate(template);
      setIsCreating(false);
      onTemplatesUpdated();
    } catch (error) {
      showError('Failed to save template');
      console.error('Error saving template:', error);
    }
  };

  const handleEdit = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setIsCreating(false);
    setFormData({
      name: template.name,
      subject: template.subject,
      body: template.body,
      isDefault: template.isDefault,
    });
    setErrors({});
  };

  const handleDelete = async (template: EmailTemplate) => {
    if (!window.confirm(`Are you sure you want to delete the template "${template.name}"?`)) return;

    try {
      await supabaseEmailTemplateStorage.deleteEmailTemplate(template.id);
      setTemplates((prev) => prev.filter((t) => t.id !== template.id));
      if (editingTemplate?.id === template.id) {
        resetForm();
      }
      showSuccess('Template deleted successfully');
      onTemplatesUpdated();
    } catch (error) {
      showError('Failed to delete template');
      console.error('Error deleting template:', error);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', subject: '', body: '', isDefault: false });
    setEditingTemplate(null);
    setIsCreating(false);
    setErrors({});
    setShowVisualEditor(false);
  };

  const startNewTemplate = () => {
    resetForm();
    setIsCreating(true);
  };

  const insertPlaceholder = (placeholder: string) => {
    const target = lastFocusedFieldRef.current;

    if (target === 'subject') {
      const input = subjectInputRef.current;
      if (input) {
        const start = input.selectionStart ?? formData.subject.length;
        const end = input.selectionEnd ?? start;
        const next = formData.subject.slice(0, start) + placeholder + formData.subject.slice(end);
        setFormData((prev) => ({ ...prev, subject: next }));
        setTimeout(() => {
          input.focus();
          const pos = start + placeholder.length;
          input.setSelectionRange(pos, pos);
        }, 0);
      } else {
        setFormData((prev) => ({ ...prev, subject: prev.subject + placeholder }));
      }
      return;
    }

    if (target === 'body' && showVisualEditor) {
      emailEditorRef.current?.saveSelection();
      emailEditorRef.current?.insertText(placeholder);
      return;
    }

    const textarea = document.getElementById('emailBodyEditor') as HTMLTextAreaElement | null;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const next = formData.body.slice(0, start) + placeholder + formData.body.slice(end);
      setFormData((prev) => ({ ...prev, body: next }));
      setTimeout(() => {
        textarea.focus();
        const pos = start + placeholder.length;
        textarea.setSelectionRange(pos, pos);
      }, 0);
      return;
    }

    setFormData((prev) => ({ ...prev, body: prev.body + placeholder }));
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (typeof value === 'string' && errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const isEditing = isCreating || editingTemplate !== null;

  return (
    <>
      <FloatingWorkspacePanel
        panelId="email-templates"
        title="Manage Email Templates"
        defaultBounds={{ x: 32, y: 28, width: 1400, height: 820 }}
        minSize={{ width: 640, height: 400 }}
        backdrop={showCourseWordingModal ? 'none' : 'full'}
        baseZIndex={9999}
        onClose={onClose}
        headerLeading={
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center rounded-lg px-2 py-1.5 text-slate-600 hover:bg-slate-100"
            onPointerDown={(e) => e.stopPropagation()}
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            <span className="text-sm font-medium">Back</span>
          </button>
        }
        icon={
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600">
            <Settings className="h-4 w-4 text-white" />
          </div>
        }
        headerActions={
          <button
            type="button"
            onClick={() => setShowCourseWordingModal(true)}
            className="inline-flex items-center rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-800 hover:bg-emerald-100"
          >
            <BookOpen className="mr-2 h-4 w-4" />
            Course email wording
          </button>
        }
        footer={
          <div className="flex items-center justify-between px-5 py-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-300 px-5 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
            >
              Close
            </button>
            <div className="flex gap-2">
              {isEditing && (
                <>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="rounded-xl border border-slate-300 px-5 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleSave()}
                    className="inline-flex items-center rounded-xl bg-emerald-600 px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-700"
                  >
                    <Save className="mr-2 h-4 w-4" />
                    {editingTemplate && !isCreating ? 'Update Template' : 'Save Template'}
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={onTemplatesUpdated}
                className="rounded-xl bg-slate-800 px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-900"
              >
                Done
              </button>
            </div>
          </div>
        }
      >
        <div className="flex min-h-0 flex-1 flex-col">
        {isEditing && (
          <div className="shrink-0 border-b border-slate-200 bg-white px-5 py-3">
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-12 lg:items-end">
              <div className="lg:col-span-3">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Template name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                    errors.name ? 'border-red-300 bg-red-50' : 'border-slate-200'
                  }`}
                  placeholder="e.g. Course Booking Confirmation"
                />
                {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
              </div>

              <div className="lg:col-span-5">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Email subject *</label>
                <input
                  ref={subjectInputRef}
                  type="text"
                  value={formData.subject}
                  onChange={(e) => handleInputChange('subject', e.target.value)}
                  onFocus={() => {
                    lastFocusedFieldRef.current = 'subject';
                  }}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                    errors.subject ? 'border-red-300 bg-red-50' : 'border-slate-200'
                  }`}
                  placeholder="e.g. Course Booking Confirmation - {{CourseName}}"
                />
                {errors.subject && <p className="mt-1 text-xs text-red-600">{errors.subject}</p>}
              </div>

              <div className="flex flex-wrap items-center gap-4 lg:col-span-4 lg:justify-end">
                <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={formData.isDefault}
                    onChange={(e) => handleInputChange('isDefault', e.target.checked)}
                    className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-slate-300 rounded"
                  />
                  Default template
                </label>
                <button
                  type="button"
                  onClick={() => setShowVisualEditor((v) => !v)}
                  className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    showVisualEditor
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <Edit className="w-3.5 h-3.5 mr-1.5" />
                  {showVisualEditor ? 'Hide visual editor' : 'Show visual editor'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Four-column body */}
        <div className="flex-1 min-h-0 overflow-x-auto">
          <div className="grid h-full min-h-0 min-w-[1400px] grid-cols-[minmax(220px,1fr)_minmax(320px,2fr)_minmax(320px,2fr)_minmax(240px,1fr)] divide-x divide-slate-200">
            {/* Column 1 — templates */}
            <section className="flex min-h-0 flex-col bg-slate-50/60">
              <div className="flex shrink-0 items-center justify-between border-b border-slate-200 p-4">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-800">Templates</h3>
                <button
                  onClick={startNewTemplate}
                  className="inline-flex items-center rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-emerald-700"
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Add
                </button>
              </div>

              <div className="flex-1 space-y-2 overflow-y-auto p-3">
                {isLoading ? (
                  <div className="py-10 text-center text-sm text-slate-500">Loading templates…</div>
                ) : templates.length === 0 ? (
                  <div className="px-3 py-10 text-center">
                    <Mail className="mx-auto mb-3 h-10 w-10 text-slate-300" />
                    <p className="text-sm text-slate-600">No templates yet. Click Add to create one.</p>
                  </div>
                ) : (
                  templates.map((template) => {
                    const isSelected = editingTemplate?.id === template.id && !isCreating;
                    return (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => handleEdit(template)}
                        className={`w-full rounded-xl border p-3 text-left transition-all ${
                          isSelected
                            ? 'border-emerald-400 bg-white shadow-sm ring-1 ring-emerald-200'
                            : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="mb-1 flex items-center gap-2">
                              <span className="truncate text-sm font-semibold text-slate-800">{template.name}</span>
                              {template.isDefault && (
                                <span className="shrink-0 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800">
                                  Default
                                </span>
                              )}
                            </div>
                            <p className="truncate text-xs text-slate-500">{template.subject}</p>
                          </div>
                          <div className="flex shrink-0 gap-1">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(template);
                              }}
                              className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
                              title="Edit"
                            >
                              <Edit className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(template);
                              }}
                              className="rounded-lg p-1.5 text-red-500 hover:bg-red-50"
                              title="Delete"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}

                {isCreating && (
                  <div className="rounded-xl border border-dashed border-emerald-400 bg-emerald-50/50 p-3 text-sm font-medium text-emerald-800">
                    New template (unsaved)
                  </div>
                )}
              </div>
            </section>

            {/* Column 2 — raw HTML (+ optional visual editor) */}
            <section className="flex min-h-0 flex-col">
              <div className="flex shrink-0 items-center gap-2 border-b border-slate-200 px-4 py-3">
                <Code2 className="h-4 w-4 text-slate-500" />
                <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-800">Raw HTML</h3>
              </div>

              {!isEditing ? (
                <div className="flex flex-1 flex-col items-center justify-center p-6 text-center text-slate-400">
                  <Mail className="mb-3 h-10 w-10 text-slate-300" />
                  <p className="text-sm">Select a template to edit HTML</p>
                </div>
              ) : (
                <div className="flex min-h-0 flex-1 flex-col">
                  {showVisualEditor && (
                    <div className="shrink-0 border-b border-slate-200 p-3">
                      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        Visual editor
                      </p>
                      <EmailEditor
                        ref={emailEditorRef}
                        content={formData.body}
                        onChange={(value) => handleInputChange('body', value)}
                        onFocus={() => {
                          lastFocusedFieldRef.current = 'body';
                        }}
                        placeholder="Dear {{ContactFirstName}}, thank you for your booking…"
                        height={200}
                      />
                    </div>
                  )}

                  <div className="flex min-h-0 flex-1 flex-col p-3">
                    <textarea
                      id="emailBodyEditor"
                      value={formData.body}
                      onChange={(e) => handleInputChange('body', e.target.value)}
                      onFocus={() => {
                        lastFocusedFieldRef.current = 'html';
                      }}
                      className={`min-h-0 flex-1 resize-none rounded-xl border px-3 py-2 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                        errors.body ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-slate-50'
                      }`}
                      placeholder='<div style="font-family: Arial, sans-serif;">Hello {{ContactFirstName}}</div>'
                      spellCheck={false}
                    />
                    {errors.body && <p className="mt-1 shrink-0 text-xs text-red-600">{errors.body}</p>}
                  </div>
                </div>
              )}
            </section>

            {/* Column 3 — preview */}
            <section className="flex min-h-0 flex-col bg-slate-50/30">
              <div className="flex shrink-0 items-center gap-2 border-b border-slate-200 px-4 py-3">
                <Eye className="h-4 w-4 text-slate-500" />
                <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-800">
                  Preview (sample data)
                </h3>
              </div>

              {!isEditing ? (
                <div className="flex flex-1 flex-col items-center justify-center p-6 text-center text-slate-400">
                  <Eye className="mb-3 h-10 w-10 text-slate-300" />
                  <p className="text-sm">Preview appears when a template is selected</p>
                </div>
              ) : (
                <div className="flex min-h-0 flex-1 flex-col p-3">
                  <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white">
                    <div className="shrink-0 border-b border-slate-100 bg-slate-50 px-4 py-2">
                      <p className="text-[11px] uppercase tracking-wide text-slate-500">Subject</p>
                      <p className="truncate text-sm font-medium text-slate-800">{previewSubject}</p>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4">
                      <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
                    </div>
                  </div>
                </div>
              )}
            </section>

            {/* Column 4 — placeholders */}
            <section className="flex min-h-0 flex-col bg-slate-50/40">
              <div className="shrink-0 border-b border-slate-200 p-4">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-800">Placeholders</h3>
                <p className="mt-1 text-xs text-slate-500">
                  Click to insert at cursor. Sample values show how they render.
                </p>
              </div>

              <div className="flex-1 space-y-2 overflow-y-auto p-3">
                {PLACEHOLDER_META.map(({ token, description }) => {
                  const sampleValue = samplePlaceholders[token] ?? '';
                  return (
                    <button
                      key={token}
                      type="button"
                      disabled={!isEditing}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => insertPlaceholder(token)}
                      className={`w-full rounded-xl border p-3 text-left transition-all ${
                        isEditing
                          ? 'cursor-pointer border-slate-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/40'
                          : 'cursor-not-allowed border-slate-100 bg-slate-100/50 opacity-60'
                      }`}
                    >
                      <code className="break-all text-xs font-semibold text-emerald-700">{token}</code>
                      <p className="mt-1 text-[11px] text-slate-500">{description}</p>
                      <div className="mt-2 border-t border-slate-100 pt-2">
                        <p className="mb-0.5 text-[10px] uppercase tracking-wide text-slate-400">In email</p>
                        <p className="break-words text-xs text-slate-700">{formatSamplePreview(sampleValue)}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>
          </div>
        </div>
        </div>
      </FloatingWorkspacePanel>

      {showCourseWordingModal && (
        <ManageCourseEmailContentModal onClose={() => setShowCourseWordingModal(false)} />
      )}
    </>
  );
}
